package com.zq.webproject.ui.earnings


import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.input.pointer.util.VelocityTracker
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.zq.webproject.model.IncomeRecord
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.Font
import webproject.composeapp.generated.resources.Res
import webproject.composeapp.generated.resources.font_song

// 统一字体（使用宋体）
private val CaiFont: FontFamily
    @Composable
    get() = FontFamily(Font(Res.font.font_song))

/**
 * 收益记录界面
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EarningsRecordScreen(
    onBackClick: () -> Unit
) {
    println("EarningsRecordScreen - 渲染收益记录页面") // 日志输出

    val viewModel = remember { EarningsRecordViewModel() }

    // 页面加载时获取收益记录
    LaunchedEffect(Unit) {
        println("EarningsRecordScreen - 开始加载收益记录") // 日志输出
        viewModel.loadIncomeRecords()
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = "收益记录",
                        fontFamily = CaiFont,
                        fontWeight = FontWeight.Bold
                    )
                },
                navigationIcon = {
                    IconButton(onClick = {
                        println("EarningsRecordScreen - 点击返回按钮") // 日志输出
                        onBackClick()
                    }) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "返回"
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color(0xFF1E88E5),
                    titleContentColor = Color.White,
                    navigationIconContentColor = Color.White
                )
            )
        }
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF5F5F5))
        ) {
            when {
                viewModel.isLoading && viewModel.incomeRecords.isEmpty() -> {
                    // 首次加载中状态
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(
                            color = Color(0xFF1E88E5)
                        )
                    }
                }
                viewModel.errorMessage != null -> {
                    // 错误状态
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Text(
                                text = viewModel.errorMessage ?: "加载失败",
                                color = Color.Red,
                                fontSize = 16.sp,
                                fontFamily = CaiFont
                            )
                            Spacer(modifier = Modifier.height(16.dp))
                            Button(
                                onClick = {
                                    println("EarningsRecordScreen - 点击重试按钮") // 日志输出
                                    viewModel.loadIncomeRecords()
                                },
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = Color(0xFF1E88E5)
                                )
                            ) {
                                Text("重试", fontFamily = CaiFont)
                            }
                        }
                    }
                }
                viewModel.incomeRecords.isEmpty() -> {
                    // 空状态
                    EmptyState()
                }
                else -> {
                    // 显示收益记录列表（带下拉刷新和上拉加载）
                    IncomeRecordListWithRefresh(
                        viewModel = viewModel
                    )
                }
            }
        }
    }
}

/**
 * 收益记录列表（带下拉刷新和上拉加载）
 */
@Composable
fun IncomeRecordListWithRefresh(
    viewModel: EarningsRecordViewModel
) {
    val listState = rememberLazyListState()
    val coroutineScope = rememberCoroutineScope()
    val isRefreshing = viewModel.isRefreshing
    val isLoadingMore = viewModel.isLoadingMore
    
    // 下拉刷新相关状态
    var pullOffset by remember { mutableStateOf(0f) }
    var isDragging by remember { mutableStateOf(false) }
    val refreshThreshold = 80f // 触发刷新的阈值（像素）
    val dragResistance = 0.3f // 拖动阻力系数，越小越费力
    
    // 监测滚动到底部，触发加载更多
    LaunchedEffect(listState) {
        snapshotFlow {
            val layoutInfo = listState.layoutInfo
            val totalItems = layoutInfo.totalItemsCount
            val lastVisibleItem = layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: 0
            
            // 当滚动到倒数3个项目时开始加载
            lastVisibleItem >= totalItems - 3 && totalItems > 0
        }.collect { shouldLoadMore ->
            if (shouldLoadMore && !isLoadingMore && !isRefreshing && viewModel.hasMore) {
                println("IncomeRecordList - 触发加载更多") // 日志输出
                viewModel.loadMore()
            }
        }
    }
    
    // 刷新完成后重置偏移
    LaunchedEffect(isRefreshing) {
        if (!isRefreshing && pullOffset > 0) {
            // 平滑回弹到初始位置
            while (pullOffset > 0) {
                pullOffset = (pullOffset - 10f).coerceAtLeast(0f)
                delay(16) // 约60fps
            }
        }
    }
    
    Box(modifier = Modifier.fillMaxSize()) {
        // 下拉刷新指示器（固定在顶部，随 pullOffset 显示）
        if (pullOffset > 0 || isRefreshing) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .offset(y = (pullOffset - 60).coerceAtLeast(0f).dp)
                    .padding(vertical = 8.dp),
                contentAlignment = Alignment.Center
            ) {
                Surface(
                    shape = RoundedCornerShape(20.dp),
                    color = Color.White,
                    shadowElevation = 4.dp
                ) {
                    Row(
                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        if (isRefreshing) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(20.dp),
                                color = Color(0xFF1E88E5),
                                strokeWidth = 2.dp
                            )
                            Text(
                                text = "刷新中...",
                                fontSize = 14.sp,
                                color = Color.Gray,
                                fontFamily = CaiFont
                            )
                        } else {
                            // 显示下拉进度
                            val progress = (pullOffset / refreshThreshold).coerceIn(0f, 1f)
                            CircularProgressIndicator(
                                progress = { progress },
                                modifier = Modifier.size(20.dp),
                                color = Color(0xFF1E88E5),
                                strokeWidth = 2.dp
                            )
                            Text(
                                text = if (pullOffset >= refreshThreshold) "松开刷新" else "下拉刷新",
                                fontSize = 14.sp,
                                color = Color.Gray,
                                fontFamily = CaiFont
                            )
                        }
                    }
                }
            }
        }
        
        LazyColumn(
            state = listState,
            contentPadding = PaddingValues(bottom = 16.dp),
            modifier = Modifier
                .fillMaxSize()
                .offset(y = pullOffset.dp)
                .pointerInput(Unit) {
                    awaitPointerEventScope {
                        while (true) {
                            val event = awaitPointerEvent()
                            
                            event.changes.forEach { change ->
                                val position = change.position
                                val previousPosition = change.previousPosition
                                val deltaY = position.y - previousPosition.y
                                
                                when {
                                    // 按下开始
                                    change.pressed && change.previousPressed.not() -> {
                                        // 检查是否在列表顶部
                                        val isAtTop = listState.firstVisibleItemIndex == 0 && 
                                                      listState.firstVisibleItemScrollOffset == 0
                                        if (isAtTop && !isRefreshing) {
                                            isDragging = true
                                        }
                                    }
                                    // 拖动中
                                    change.pressed && isDragging -> {
                                        if (deltaY > 0) {
                                            // 向下拖动，增加阻力
                                            pullOffset = (pullOffset + deltaY * dragResistance).coerceIn(0f, refreshThreshold * 1.2f)
                                            change.consume()
                                        } else if (pullOffset > 0) {
                                            // 向上拖动但还有偏移
                                            pullOffset = (pullOffset + deltaY * dragResistance).coerceAtLeast(0f)
                                            if (pullOffset > 0) {
                                                change.consume()
                                            } else {
                                                isDragging = false
                                            }
                                        } else {
                                            isDragging = false
                                        }
                                    }
                                    // 松开手
                                    !change.pressed && isDragging -> {
                                        isDragging = false
                                        if (pullOffset >= refreshThreshold) {
                                            // 触发刷新
                                            println("触发下拉刷新: pullOffset=$pullOffset")
                                            viewModel.refresh()
                                        } else {
                                            // 未达到阈值，回弹
                                            coroutineScope.launch {
                                                while (pullOffset > 0) {
                                                    pullOffset = (pullOffset - 15f).coerceAtLeast(0f)
                                                    delay(16)
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
        ) {
            items(viewModel.incomeRecords) { record ->
                println("IncomeRecordList - 渲染收益记录: ${record.incomeAmount}") // 日志输出
                IncomeRecordItem(record)
            }
            
            // 加载更多指示器
            if (isLoadingMore) {
                item {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(24.dp),
                                color = Color(0xFF1E88E5)
                            )
                            Text(
                                text = "加载中...",
                                fontSize = 14.sp,
                                color = Color.Gray,
                                fontFamily = CaiFont
                            )
                        }
                    }
                }
            }
            
            // 没有更多数据提示
            if (!viewModel.hasMore && viewModel.incomeRecords.isNotEmpty() && !isLoadingMore) {
                item {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(vertical = 24.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = "—— 没有更多了 ——",
                            fontSize = 14.sp,
                            color = Color.LightGray,
                            fontFamily = CaiFont
                        )
                    }
                }
            }
        }
    }
}

/**
 * 单个收益记录卡片
 */
@Composable
fun IncomeRecordItem(record: IncomeRecord) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 8.dp),
        shape = RoundedCornerShape(12.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        colors = CardDefaults.cardColors(
            containerColor = Color.White
        )
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
        ) {
            // 收益金额
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "收益金额",
                    fontSize = 14.sp,
                    color = Color.Gray,
                    fontFamily = CaiFont
                )
                Text(
                    text = "+${record.incomeAmount}元",
                    fontSize = 18.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFFFF6B6B),
                    fontFamily = CaiFont
                )
            }
            
            Spacer(modifier = Modifier.height(8.dp))
            
            // 收益日期
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text(
                    text = "收益日期",
                    fontSize = 14.sp,
                    color = Color.Gray,
                    fontFamily = CaiFont
                )
                Text(
                    text = record.incomeDate,
                    fontSize = 14.sp,
                    color = Color(0xFF333333),
                    fontFamily = CaiFont
                )
            }
            
            // 订单号（如果有）
            record.orderNo?.let { orderNo ->
                Spacer(modifier = Modifier.height(8.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        text = "订单号",
                        fontSize = 14.sp,
                        color = Color.Gray,
                        fontFamily = CaiFont
                    )
                    Text(
                        text = orderNo,
                        fontSize = 12.sp,
                        color = Color(0xFF666666),
                        fontFamily = CaiFont
                    )
                }
            }
        }
    }
}

/**
 * 空状态
 */
@Composable
fun EmptyState() {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFFF5F5F5)),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                text = "暂无收益记录",
                fontSize = 16.sp,
                color = Color.Gray,
                fontFamily = CaiFont
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = "快去邀请好友购买商品吧",
                fontSize = 14.sp,
                color = Color.LightGray,
                fontFamily = CaiFont
            )
        }
    }
}
